import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  InputBase,
  CircularProgress,
  Divider,
  Fade,
  Skeleton,
} from "@mui/material";
import { SearchNormal } from "iconsax-react";
import { toast } from "react-hot-toast";
import { slimScrollbarSx } from "../../../utils/scrollbarStyles";

import {
  fetchInboxTeamData,
  assignContact,
  clearAssignState,
} from "../../../redux/Slice/Inbox/inboxSettingsSlice";
import { isSubscriptionBlockedError } from "../../../utils/subscriptionError";
import { useSubscriptionGuard } from "../../../hooks/useSubscriptionGuard";
import { sortByName } from "../../../utils/sortByName";

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

// Skeleton loader component
const ListItemSkeleton = () => (
  <ListItem sx={{ padding: "6px 10px" }}>
    <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1.25 }} />
    <Skeleton variant="text" width="60%" height={18} />
  </ListItem>
);

const AssignBox = ({ handleClose, contactId: contactIdProp, onClose }) => {
  const dispatch = useDispatch();
  const { guard } = useSubscriptionGuard();

  // Local states
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 200); // Reduced debounce time

  // Access Redux states
  const { userLoading, userError } = useSelector(
    (state) => state.userDetails
  );

  // Add permission selectors
  const TeamPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Team || {}
  );

  const TeamMemberPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.TeamMember || {}
  );

  const ConversationPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Conversation || {}
  );


  // Create separate loading states for teams and team members
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [teamsError, setTeamsError] = useState(null);
  const [teamMembersError, setTeamMembersError] = useState(null);
  const [teamsFetched, setTeamsFetched] = useState(false);
  const [teamMembersFetched, setTeamMembersFetched] = useState(false);
  const [assigningId, setAssigningId] = useState(null);

  // Use safe selectors with array checks
  const teams = useSelector((state) => {
    const teamsData = state.inboxSettings.teams;
    return Array.isArray(teamsData) ? teamsData : [];
  });

  const teamMembers = useSelector((state) => {
    const membersData = state.inboxSettings.teamMembers;
    return Array.isArray(membersData) ? membersData : [];
  });

  const { assignLoading, assignError, assignStatus } = useSelector(
    (state) => state.inboxSettings
  );
  // contactId prop (2026-08-19, for MessageCard.jsx's "..." menu — assigning a
  // contact from the list must target the RIGHT-CLICKED row, not whatever chat
  // happens to be open) falls back to the original Redux read so ChatboxNavbar's
  // existing usage (no prop passed) stays byte-identical.
  const contactIdFromReduxChat = useSelector((state) => state.inboxInfo.contactInfo?._id);
  const contactId = contactIdProp || contactIdFromReduxChat;

  // Fetch teams + team members together (single combined, cached request) when
  // component mounts if the user has permission to view either. The two
  // domains still surface loading/error independently below since a caller
  // can have only one of Team.View/TeamMember.View.
  useEffect(() => {
    const needsTeams = TeamPermissions.View && teams.length === 0 && !teamsFetched;
    const needsMembers = TeamMemberPermissions.View && teamMembers.length === 0 && !teamMembersFetched;
    if (!needsTeams && !needsMembers) return;

    if (needsTeams) {
      setTeamsLoading(true);
      setTeamsError(null);
    }
    if (needsMembers) {
      setTeamMembersLoading(true);
      setTeamMembersError(null);
    }

    dispatch(fetchInboxTeamData())
      .unwrap()
      .catch((error) => {
        if (needsTeams && !String(error).includes("No teams found")) {
          setTeamsError(error);
        }
        if (
          needsMembers &&
          !String(error).includes("No team members found") &&
          !String(error).includes("No teams found")
        ) {
          setTeamMembersError(error);
        }
      })
      .finally(() => {
        if (needsTeams) {
          setTeamsFetched(true);
          setTeamsLoading(false);
        }
        if (needsMembers) {
          setTeamMembersFetched(true);
          setTeamMembersLoading(false);
        }
      });
  }, [
    dispatch,
    TeamPermissions.View,
    TeamMemberPermissions.View,
    teamsFetched,
    teamMembersFetched,
    teams.length,
    teamMembers.length,
  ]);

  // Close the dropdown FIRST, then fire the toast (Rahim, 2026-08-31: "close
  // the team member dropdown from the top nav then make the toast
  // notification") — was the reverse before: a local Snackbar rendered
  // *inside* this popover, kept both open together for 2s so the message
  // was visible before either closed, which read as the toast and dropdown
  // fighting for the same top-right corner. Using the app-level toast()
  // instead of a local Snackbar means the message survives this component
  // unmounting the instant the popover closes, so there's no need to keep
  // the dropdown open just to show it anymore. On error the dropdown still
  // stays open (unchanged) so the user can pick someone else.
  useEffect(() => {
    if (assignStatus === "successful") {
      handleClose();
      toast.success("Contact assigned successfully!");
      dispatch(clearAssignState());
    } else if (assignError) {
      if (isSubscriptionBlockedError(assignError)) {
        dispatch(clearAssignState()); // interceptor already toasted; clear silently
      } else {
        toast.error(assignError);
        dispatch(clearAssignState());
      }
    }
  }, [assignStatus, assignError, handleClose, dispatch]);

  const handleAssignContact = async (Id, type, Name) => {
    if (assignLoading) return;
    if (!await guard([])) return;

    const canAssignToTeam =
      ConversationPermissions.EditAll ||
      ConversationPermissions.EditAssignedToTeam;
    const canAssignToMember =
      ConversationPermissions.EditAll ||
      ConversationPermissions.EditAssignedToTeamMembers;

    if (type === "team" && !canAssignToTeam) {
      toast.error("You don't have permission to assign to a team");
      return;
    }
    if (type === "teammember" && !canAssignToMember) {
      toast.error("You don't have permission to assign to a team member");
      return;
    }
    setAssigningId(Id);
    dispatch(assignContact({ Id, contactId, type, Name }))
    .finally(() => setAssigningId(null));
  };

  // Memoized filtered lists
  const filteredTeams = useMemo(
    () =>
      sortByName(teams, (team) => team.teamName).filter((team) =>
        team.teamName.toLowerCase().includes(debouncedSearch.toLowerCase())
      ),
    [teams, debouncedSearch]
  );
  const filteredTeamMembers = useMemo(
    () =>
      sortByName(teamMembers).filter((member) =>
        member.name.toLowerCase().includes(debouncedSearch.toLowerCase())
      ),
    [teamMembers, debouncedSearch]
  );

  // Only show full loading state when user details are loading (critical for permissions)
  if (userLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
          minWidth: "340px",
        }}
      >
        {/* <CircularProgress size={32} /> */}
      </Box>
    );
  }

  // Only show full error state for critical user errors
  if (userError) {
    return (
      <Box
        sx={{
          textAlign: "center",
          padding: "20px",
          color: (theme) => theme.palette.error.main,
        }}
      >
        <Typography variant="body2">
          {userError || "Something went wrong. Please try again."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
  sx={{
    width: "100%",
    // Smaller overall footprint — was a fixed 600px-tall box regardless of
    // how few rows there were, reading as an oversized empty modal (Rahim,
    // 2026-08-20: "its a little too big"). maxHeight + auto instead of a
    // fixed height so it shrinks to content and only scrolls past the cap.
    minWidth: { xs: "260px", sm: "340px", md: "340px", lg: "340px", xl: "340px" },
    maxWidth: { xs: "290px", sm: "360px", md: "360px", lg: "360px", xl: "360px" },
    maxHeight: { xs: "360px", sm: "420px", md: "420px", lg: "420px", xl: "420px" },
    height: "auto",
    display: "flex",
    flexDirection: "column",
    borderRadius: 0,
    overflow: "hidden",
    // Was background.default — didn't match the composer card it opens from
    // (background.paper), a genuinely different shade in both modes (Rahim,
    // 2026-08-24: "make the assign member dropdown match the text box one").
    backgroundColor: (theme) => theme.palette.background.paper,
    boxShadow: "none",
    border: "none",
    position: "relative",
  }}
>

      {/* Search field + close button share one row now, matching the
          Inbox dialogs' own search-bar formula (bordered rounded box,
          iconsax search glyph) instead of a floating-label MUI TextField
          (Rahim, 2026-08-20: "our new better ui... proper gap"). */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          p: 1,
          pb: 0.75,
          bgcolor: (theme) => theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.1,
            py: 0.7,
            borderRadius: "10px",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            transition: "border-color 0.15s ease",
            "&:focus-within": {
              borderColor: (theme) => theme.palette.primary.main,
            },
          }}
        >
          <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", flexShrink: 0 }}>
            <SearchNormal size="100%" color="currentColor" />
          </Box>
          <InputBase
            placeholder="Search teams and members"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ fontSize: "0.8125rem", flex: 1, color: "text.primary" }}
          />
        </Box>
        {/* Close (X) button removed — the dropdown already closes via
            outside-click, and the search bar's own flex:1 now fills the row
            it used to share with this button (Rahim, 2026-08-24: "remove the
            cross and increase the search bar"). */}
      </Box>

      {/* Content Container */}
      <Box sx={{
         flex: 1,
         overflowY: "auto",
         overflowX: "hidden",
         // Was a dark, near-black inline scrollbar (Rahim, 2026-08-28: "very
         // dark make it light") — swapped for the app's own shared light
         // formula instead of hand-tuning another one-off.
         ...slimScrollbarSx,
      }}>
      {/* Teams Section */}
      <Box>
          <Box sx={{ px: 1.25, pt: 1, pb: 0.4 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              fontWeight: 600,
                fontSize: "12px",
            }}
          >
            Teams
          </Typography>
        </Box>

          <Divider sx={{
            borderColor: (theme) => theme.palette.divider,
            opacity: 0.5,
            mx: 1.25
          }} />

          <List sx={{ py: 0.5 }}>
          {!TeamPermissions.View ? (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontStyle: "italic",
                  padding: { xs: "8px", sm: "12px", md: "16px", lg: "16px", xl: "16px" },
                textAlign: "center",
                  fontSize: { xs: "11px", sm: "12px", md: "13px", lg: "13px", xl: "13px" },
              }}
            >
              You don't have permission to view teams
            </Typography>
          ) : teamsLoading ? (
              <Box sx={{ py: 1 }}>
                {[1, 2, 3].map((item) => (
                  <ListItemSkeleton key={item} />
                ))}
            </Box>
          ) : teamsError ? (
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.error.main,
                textAlign: "center",
                  padding: "16px",
                  fontSize: "13px",
              }}
            >
              Failed to load teams: {teamsError}
            </Typography>
          ) : filteredTeams.length > 0 ? (
            filteredTeams.map((team) => (
                <Fade in={true} key={team.id}>
              <ListItem
                disablePadding
                sx={{ margin: "2px 6px", width: "auto" }}
              >
                <ListItemButton
                  sx={{
                      padding: "6px 10px",
                      borderRadius: "8px",
                      cursor: "pointer",
                  "&:hover": {
                    // Same rail-row hover formula as InboxSettings.jsx's Team/
                    // Lifecycle sections — the app-wide dropdown-row standard,
                    // instead of a generic action.hover + slide (Rahim, 2026-08-20).
                    backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
                  },
                }}
                disabled={assignLoading || !(ConversationPermissions.EditAll || ConversationPermissions.EditAssignedToTeam)}
                onClick={() => handleAssignContact(team.id, "team", team.teamName)}
              >
                <Avatar
                  sx={{
                    bgcolor: (theme) => theme.palette.primary.main,
                    width: 24,
                    height: 24,
                    mr: 1.25,
                        fontSize: "11px",
                        fontWeight: 600,
                  }}
                >
                  {team.teamName[0]}
                </Avatar>
                    <ListItemText
                      primary={team.teamName}
                      primaryTypographyProps={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "text.primary",
                      }}
                    />
                    {assignLoading && assigningId === team.id && (
  <CircularProgress size={14} sx={{ ml: 1, color: "primary.main" }} />
)}
              </ListItemButton>
              </ListItem>
                </Fade>
            ))
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.text.secondary,
                textAlign: "center",
                  padding: "16px",
                  fontSize: "13px",
                  fontStyle: "italic",
              }}
            >
              No teams found
            </Typography>
          )}
        </List>
      </Box>

      {/* Team Members Section */}
      <Box>
          <Box sx={{ px: 1.25, pt: 1, pb: 0.4 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              fontWeight: 600,
                fontSize: "12px",
            }}
          >
            Team Members
          </Typography>
        </Box>

          <Divider sx={{
            borderColor: (theme) => theme.palette.divider,
            opacity: 0.5,
            mx: 1.25
          }} />

          <List sx={{ py: 0.5 }}>
          {!TeamMemberPermissions.View ? (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontStyle: "italic",
                  padding: { xs: "8px", sm: "12px", md: "16px", lg: "16px", xl: "16px" },
                textAlign: "center",
                  fontSize: { xs: "11px", sm: "12px", md: "13px", lg: "13px", xl: "13px" },
              }}
            >
              You don't have permission to view team members
            </Typography>
          ) : teamMembersLoading ? (
              <Box sx={{ py: 1 }}>
                {[1, 2, 3, 4].map((item) => (
                  <ListItemSkeleton key={item} />
                ))}
            </Box>
          ) : teamMembersError ? (
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.error.main,
                textAlign: "center",
                  padding: "16px",
                  fontSize: "13px",
              }}
            >
              Failed to load team members: {teamMembersError}
            </Typography>
          ) : filteredTeamMembers.length > 0 ? (
              filteredTeamMembers.map((member, index) => (
                <Fade in={true} key={member.id} timeout={index * 100}>
              <ListItem
                disablePadding
                sx={{ margin: "2px 6px", width: "auto" }}
              >
                <ListItemButton
                  sx={{
                      padding: "6px 10px",
                      borderRadius: "8px",
                      cursor: "pointer",
                  "&:hover": {
                    backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
                  },
                }}
                disabled={assignLoading || !(ConversationPermissions.EditAll || ConversationPermissions.EditAssignedToTeamMembers)}
                onClick={() => handleAssignContact(member.id, "teammember", member.name)}
              >
                <Avatar
                  sx={{
                    bgcolor: (theme) => theme.palette.secondary.main,
                    width: 24,
                    height: 24,
                    mr: 1.25,
                        fontSize: "11px",
                        fontWeight: 600,
                  }}
                >
                  {member.name[0]}
                </Avatar>
                    <ListItemText
                      primary={member.name}
                      primaryTypographyProps={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "text.primary",
                      }}
                    />
                    {assignLoading && assigningId === member.id && (
  <CircularProgress size={14} sx={{ ml: 1, color: "primary.main" }} />)}
              </ListItemButton>
              </ListItem>
                </Fade>
            ))
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.text.secondary,
                textAlign: "center",
                  padding: "16px",
                  fontSize: "13px",
                  fontStyle: "italic",
              }}
            >
              No team members found
            </Typography>
          )}
        </List>
        </Box>
      </Box>

    </Box>
  );
};

export default AssignBox;